import { Currency, HYDRA, currencyEquals, WHYDRA, ChainId } from 'hydra/sdk'
import { useMemo } from 'react'
import { tryParseAmount } from '../state/swap/hooks'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useCurrencyBalance } from '../state/wallet/hooks'
import { account as accountHYDRA, hydraweb3RPC } from 'hooks/useAddHydraAccExtension'
import { getContract } from 'hydra/contracts/utils'
import { AbiWrappedHydra } from 'hydra/contracts/abi'
import { deposit, withdraw } from 'hydra/contracts/whydraFunctions'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP
}

const NOT_APPLICABLE = { wrapType: WrapType.NOT_APPLICABLE }
/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  typedValue: string | undefined
): { wrapType: WrapType; execute?: undefined | (() => Promise<any>); inputError?: string } {
  const { chainId, account } = { chainId: ChainId.MAINNET, account: accountHYDRA?.address }
  const whydraContract = getContract(hydraweb3RPC, WHYDRA[chainId].address, AbiWrappedHydra)
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency)
  // we can always parse the amount typed as the input currency, since wrapping is 1:1
  const inputAmount = useMemo(() => tryParseAmount(typedValue, inputCurrency), [inputCurrency, typedValue])
  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!whydraContract || !chainId || !inputCurrency || !outputCurrency) return NOT_APPLICABLE

    const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)
    if (inputCurrency === HYDRA && outputCurrency.name === WHYDRA[chainId].name) {
      return {
        wrapType: WrapType.WRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await deposit(whydraContract, accountHYDRA, inputAmount.toExact())
                  txReceipt.hash = txReceipt.txid
                  addTransaction(txReceipt, { summary: `Wrap ${inputAmount.toSignificant(6)} HYDRA to WHYDRA` })
                  return txReceipt
                } catch (error) {
                  console.error('Could not deposit', error)
                }
              }
            : undefined,
        inputError: sufficientBalance ? undefined : 'Insufficient HYDRA balance'
      }
    } else if (inputCurrency.name === WHYDRA[chainId].name && outputCurrency === HYDRA) {
      return {
        wrapType: WrapType.UNWRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
                try {
                  const txReceipt = await withdraw(whydraContract, accountHYDRA, inputAmount.raw.toString())
                  txReceipt.hash = txReceipt.txid
                  addTransaction(txReceipt, { summary: `Unwrap ${inputAmount.toSignificant(6)} HYDRA to WHYDRA` })
                  return txReceipt
                } catch (error) {
                  console.error('Could not withdraw', error)
                }
              }
            : undefined,
        inputError: sufficientBalance ? undefined : 'Insufficient WHYDRA balance'
      }
    } else {
      return NOT_APPLICABLE
    }
  }, [whydraContract, chainId, inputCurrency, outputCurrency, inputAmount, balance])
}
